import { motion } from "framer-motion";
import { Heart, Gift, ExternalLink } from "lucide-react";

// Custom Balloon SVG Icon Component
const BalloonIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M12 2C8.13 2 5 5.13 5 9c0 3.87 3.5 8 7 12 3.5-4 7-8.13 7-12 0-3.87-3.13-7-7-7z" />
    <path d="M12 21c-.55 0-1 .45-1 1v1c0 .55.45 1 1 1s1-.45 1-1v-1c0-.55-.45-1-1-1z" opacity="0.7" />
    <ellipse cx="12" cy="21" rx="1.5" ry="0.5" opacity="0.5" />
    <path d="M10 14c0 0 1 2 2 2s2-2 2-2" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
  </svg>
);

// Photos of Juno and Luna
const PUPPY_PHOTOS = {
  junoPuppy: "https://customer-assets-39nsmqrw.emergentagent.net/job_eyeblind-v2/artifacts/x75yrfwl_Copy%20of%20Resized_20251215_124247.JPG",
  lunaPuppy: "https://customer-assets-39nsmqrw.emergentagent.net/job_eyeblind-v2/artifacts/l4vclsso_IMG-20251215-WA0185.webp",
  bothNow: "https://customer-assets-39nsmqrw.emergentagent.net/job_eyeblind-v2/artifacts/cg6no9zs_IMG_20260718_111933.webp"
};

const CarliBirthday = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF6F0] via-[#FFF8F3] to-[#F5EBE0]">
      {/* Subtle Decorative Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#E8D5C4]/30 rounded-full blur-3xl" />
        <div className="absolute top-60 right-10 w-72 h-72 bg-[#D4A574]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-40 left-1/4 w-56 h-56 bg-[#B7C4A5]/25 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 py-16 px-4">
        <div className="max-w-3xl mx-auto">
          
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <p className="text-[#9A8478] uppercase tracking-[0.3em] text-sm mb-4">
              A Birthday Fundraiser
            </p>
            
            <h1 className="flex items-center justify-center gap-4 text-5xl md:text-7xl text-[#5C4A3D] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <BalloonIcon className="w-8 h-8 md:w-10 md:h-10 text-[#9B7BB8]" />
              Carli&apos;s Birthday
              <BalloonIcon className="w-8 h-8 md:w-10 md:h-10 text-[#9B7BB8]" />
            </h1>
            
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="h-px w-16 bg-[#9B7BB8]" />
              <Heart className="w-5 h-5 text-[#9B7BB8]" fill="#9B7BB8" />
              <div className="h-px w-16 bg-[#9B7BB8]" />
            </div>
            
            <p className="text-xl text-[#7A6B5E]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              In honor of <span className="italic">Juno</span> &amp; <span className="italic">Luna</span>
            </p>
          </motion.div>

          {/* Featured Photo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#D4A574]/20 rounded-2xl transform rotate-2" />
              <img
                src={PUPPY_PHOTOS.bothNow}
                alt="Juno and Luna"
                className="relative w-full rounded-2xl shadow-xl"
              />
            </div>
          </motion.div>

          {/* Puppy Photos - Square, Swapped */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 gap-6 mb-16"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#B7C4A5]/30 rounded-xl transform -rotate-1" />
              <div className="relative aspect-square overflow-hidden rounded-xl shadow-lg">
                <img
                  src={PUPPY_PHOTOS.lunaPuppy}
                  alt="Luna as a puppy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-[#D4A574]/30 rounded-xl transform rotate-1" />
              <div className="relative aspect-square overflow-hidden rounded-xl shadow-lg">
                <img
                  src={PUPPY_PHOTOS.junoPuppy}
                  alt="Juno as a puppy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Story Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-16"
          >
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-sm border border-[#E8D5C4]">
              <h3 className="text-3xl text-[#5C4A3D] text-center mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Their Story
              </h3>
              
              <div className="text-[#6B5B4F] text-lg leading-relaxed space-y-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <p>
                  For her birthday, <span className="font-semibold text-[#7B5BA8]">Carli</span> is asking for donations to 
                  Three Eye Blind Animal Rescue in honor of her two dogs, <span className="font-semibold text-[#7B5BA8]">Juno</span> and <span className="font-semibold text-[#7B5BA8]">Luna</span>, who were rescued by the organization.
                </p>
                
                <p>
                  Their story is a great example of what this organization does. They were surrendered to a high kill shelter 
                  with their siblings as very young puppies, along with their mama who was barely more than a puppy herself. 
                  Another rescue pulled them, but upon learning of some severe health problems with mama and one of their siblings, 
                  Three Eye Blind Animal Rescue stepped in and took them in.
                </p>
                
                <p>
                  Mama, whose body was riddled with pellet gun ammo and even a coat hanger fragment in her side, ended up needing 
                  multiple expensive life-saving surgeries. But now she and her pups are all living happy, healthy lives, as they should be!
                </p>
                
                <p className="text-center text-xl text-[#7B5BA8] pt-4 italic">
                  Please consider donating if you are able.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Donation Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-16"
          >
            <p className="text-center text-[#9A8478] uppercase tracking-[0.2em] text-sm mb-8">
              Ways to Give
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl mx-auto">
              {/* Zeffy */}
              <a
                href="https://www.zeffy.com/en-US/donation-form/carlis-birthday-fundraiser-for-three-eye-blind-animal-rescue"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-[#C4B5D4] hover:bg-[#B5A3C7] text-[#4A3D5C] py-4 px-6 rounded-xl shadow-md transition-all hover:shadow-lg"
              >
                <Gift className="w-5 h-5" />
                <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-lg">Donate via Zeffy</span>
                <ExternalLink className="w-4 h-4 opacity-60" />
              </a>
              
              {/* PayPal */}
              <a
                href="https://www.paypal.com/donate/?hosted_button_id=R3UGMJLQ8CQ9Q"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-[#A5C4D4] hover:bg-[#93B5C7] text-[#3D4A5C] py-4 px-6 rounded-xl shadow-md transition-all hover:shadow-lg"
              >
                <Gift className="w-5 h-5" />
                <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-lg">Donate via PayPal</span>
                <ExternalLink className="w-4 h-4 opacity-60" />
              </a>
              
              {/* Venmo */}
              <a
                href="https://venmo.com/u/threeEyeBlindrescue"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-[#B7C4A5] hover:bg-[#A3B391] text-[#3D4A35] py-4 px-6 rounded-xl shadow-md transition-all hover:shadow-lg"
              >
                <Gift className="w-5 h-5" />
                <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-lg">Donate via Venmo</span>
                <ExternalLink className="w-4 h-4 opacity-60" />
              </a>
              
              {/* Zelle */}
              <div className="flex flex-col items-center justify-center gap-1 bg-[#E8D5C4] text-[#5C4A3D] py-4 px-6 rounded-xl shadow-md">
                <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-lg">Donate via Zelle</span>
                <span className="text-sm text-[#7A6B5E]">
                  threeeyeblindrescue@gmail.com
                </span>
              </div>
            </div>
            
            <p className="text-center text-[#7A6B5E] mt-8 text-base" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Please mention it is in honor of <span className="italic text-[#7B5BA8]">Carli, Juno, and Luna</span>
            </p>
          </motion.div>

          {/* 501(c)(3) Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-block bg-white/60 backdrop-blur-sm rounded-xl px-8 py-6 shadow-sm border border-[#E8D5C4]">
              <p className="text-[#5C4A3D] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Three Eye Blind Animal Rescue is a registered <span className="font-semibold text-[#7B5BA8]">501(c)(3)</span> nonprofit.
                <br />
                <span className="text-[#7A6B5E]">Your donation is tax-deductible.</span>
              </p>
              
              <a
                href="https://customer-assets.emergentagent.com/job_eyeblind-v2/artifacts/q613lth5_Cash%20Flow%20%281%29.png"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#9B7BB8] hover:text-[#7B5BA8] text-sm"
              >
                See how donations are used
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="h-px w-12 bg-[#9B7BB8]/50" />
              <Heart className="w-4 h-4 text-[#9B7BB8]" fill="#9B7BB8" />
              <div className="h-px w-12 bg-[#9B7BB8]/50" />
            </div>
            <p className="text-[#9A8478] text-sm mb-4">
              Thank you for celebrating with us
            </p>
            <a 
              href="https://3eyeblindrescue.org" 
              className="text-[#7A6B5E] hover:text-[#5C4A3D] text-sm underline underline-offset-4"
            >
              Visit Three Eye Blind Animal Rescue
            </a>
          </motion.div>
          
        </div>
      </div>

      {/* Add elegant font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&display=swap');
      `}</style>
    </div>
  );
};

export default CarliBirthday;
